import type {
  UserLanguages,
  UserOnlineStatus,
  UserProfile,
} from 'generated/openapi';
import { memo } from 'react';

import defaultAvatar from '@/assets/img/default-avatar.png';
import { ProfileDuoCounts } from '@/features/profile/components/profileDuoCounts';
import { ProfileLanguageFlags } from '@/features/profile/components/profileLanguageFlags';
import { ProfileNameWithAge } from '@/features/profile/components/profileNameWithAge';
import { ProfileResponseTimeIndicator } from '@/features/profile/components/profileResponseTimeIndicator';
import { ProfileUserBadges } from '@/features/profile/components/profileUserBadges';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { cn } from '@/shared/utils/cn';

import { ProfileDetailsHeaderActions } from './actions';

type AnyUserBadge = UserProfile['badges'][number];

export interface ProfileDetailsHeaderProps {
  className?: string;
  displayName: string;
  pictureUrl: string;
  languages: UserLanguages;
  status: string;
  age: number;
  badges: AnyUserBadge[];
  userId: string;
  isReadyToPlay: boolean;
  onlineStatus: UserOnlineStatus;
  isDuo?: boolean;
}

export const ProfileDetailsHeader = memo((props: ProfileDetailsHeaderProps) => {
  const {
    className,
    pictureUrl,
    languages,
    status,
    displayName,
    age,
    badges,
    userId,
    isReadyToPlay,
    onlineStatus,
    isDuo,
  } = props;

  const reputationBadge = badges[0];

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="relative flex items-start">
        <Avatar className="size-12">
          <AvatarFallback>
            <img
              src={defaultAvatar}
              alt="Default avatar"
              className="size-full object-cover"
            />
          </AvatarFallback>
          <AvatarImage src={pictureUrl} alt={displayName} />
        </Avatar>
        <ProfileDetailsHeaderActions userId={userId} isDuo={isDuo} />
      </div>
      <div className="flex flex-col items-start gap-1.5">
        <div className="flex items-center gap-2">
          <ProfileNameWithAge name={displayName} age={age} />
        </div>
        <ProfileDuoCounts userId={userId} />
        <div className="flex items-center gap-4">
          <p className="text-cold-400 flex text-xs leading-4">
            Speaks
            <ProfileLanguageFlags
              className="ml-1"
              languageCodes={languages.speak}
            />
            , Chats in{' '}
            <ProfileLanguageFlags
              className="ml-1"
              languageCodes={languages.write}
            />
          </p>
        </div>
        {status && (
          <p className="font-nunito text-cold-400 text-xs leading-4 tracking-[0.12px]">
            {status}
          </p>
        )}
        <ProfileResponseTimeIndicator userId={userId} />
      </div>
      <ProfileUserBadges
        className="mt-2"
        reputationBadge={reputationBadge}
        isReadyToPlay={isReadyToPlay}
        onlineStatus={onlineStatus}
        userId={userId}
      />
    </div>
  );
});
